import Phaser from 'phaser';

// ────────────────────────────────────────────────────────────────────────────
// Zone-clear card glyphs — all sized for a ~48×48 footprint (cx,cy = centre).
// Drawn with Phaser Graphics so they respect the bronze/gold UI palette and
// don't rely on emoji glyph rendering (which varies per OS/browser and can
// drop the card's hit area to "where the glyph actually paints").
// ────────────────────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────────────────────
// Shrine / Altar boon runes — replaces the old pixel-art PNG icons with a
// drawn runic glyph. The outer ring signals tier (gold blessing / red curse /
// ornate altar), and the inner symbol is picked from the boon's dominant
// effect so a quick glance tells the player "HP / damage / gold / curse".
// ────────────────────────────────────────────────────────────────────────────

/** Minimal shape of a boon/altar def the rune picker cares about. Intentionally
 *  loose so we don't import the shared types here — we only need the keys on
 *  `effects` and the optional `tier` flag. */
export interface BoonRuneSource {
  effects: {
    fireResistBonus?: number; frostResistBonus?: number;
    shadowResistBonus?: number; natureResistBonus?: number;
    critDamagePct?: number; maxHpPct?: number; spellPowerPct?: number;
    autoAttackPct?: number; lifestealPct?: number; attackSpeedPct?: number;
    damagePct?: number; defPct?: number; maxMpPct?: number;
    goldMult?: number; lootChancePct?: number; relicDropMult?: number;
    bossHpMult?: number; healReceivedPct?: number;
    damagePerDeadHeroPct?: number;
    stackPerKill?: { stat: string; perStack: number; cap: number };
    cannotResurrect?: boolean; cannotRecycle?: boolean;
    cannotStay?: boolean; rarityBumpChance?: number;
  };
  tier?: 'blessing' | 'curse';
}

type BoonRuneKind =
  | 'sword' | 'heart' | 'star' | 'chevron' | 'triSlash' | 'shield'
  | 'coin'  | 'gem'   | 'flame' | 'frost'  | 'leaf'     | 'shadow'
  | 'stack' | 'lock'  | 'skull' | 'trilogy';

/** Pick the dominant inner symbol for a boon. Priority is "most representative
 *  effect first" — flags outrank numbers, numbers outrank utility resistances. */
function pickBoonRuneKind(src: BoonRuneSource): BoonRuneKind {
  const e = src.effects;
  if (e.cannotStay || e.cannotResurrect || e.cannotRecycle) return 'lock';
  if (e.stackPerKill) return 'stack';
  if (e.rarityBumpChance || e.bossHpMult || e.healReceivedPct !== undefined) return 'skull';
  // "Harmony" style — multiple big positive stats at once.
  const bigStats = [e.maxHpPct, e.damagePct, e.spellPowerPct].filter((v) => (v ?? 0) >= 0.2).length;
  if (bigStats >= 2) return 'trilogy';
  if ((e.damagePct ?? 0) !== 0 || (e.autoAttackPct ?? 0) !== 0 || (e.damagePerDeadHeroPct ?? 0) !== 0) return 'sword';
  if ((e.maxHpPct ?? 0) !== 0 || (e.lifestealPct ?? 0) !== 0) return 'heart';
  if ((e.spellPowerPct ?? 0) !== 0 || (e.maxMpPct ?? 0) !== 0) return 'star';
  if ((e.critDamagePct ?? 0) !== 0) return 'chevron';
  if ((e.attackSpeedPct ?? 0) !== 0) return 'triSlash';
  if ((e.defPct ?? 0) !== 0) return 'shield';
  if ((e.goldMult ?? 0) !== 0) return 'coin';
  if ((e.lootChancePct ?? 0) !== 0 || (e.relicDropMult ?? 0) !== 0) return 'gem';
  if ((e.fireResistBonus ?? 0) !== 0)   return 'flame';
  if ((e.frostResistBonus ?? 0) !== 0)  return 'frost';
  if ((e.natureResistBonus ?? 0) !== 0) return 'leaf';
  if ((e.shadowResistBonus ?? 0) !== 0) return 'shadow';
  return 'sword'; // safe fallback so we always render something
}

/** Draw a runic boon glyph: outer ring, inner dark chord, a rune-specific
 *  symbol, and four cardinal tick-marks on the ring for a "summoning-circle"
 *  read. Works at 20-48 px — everything scales off `sizePx`.
 *  `opts.forceAltar` treats the def as an altar offering even if no `tier`
 *  is set (used by the HUD row so altar rewards don't go red by default). */
export function drawBoonRune(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  src: BoonRuneSource,
  sizePx: number,
  opts: { forceAltar?: boolean } = {},
): void {
  const isCurse  = src.tier === 'curse';
  const isAltar  = opts.forceAltar || !('tier' in src) || src.tier === undefined;
  const ringColor   = isCurse ? 0xef4444 : isAltar ? 0xd4a060 : 0xf0c040;
  const innerColor  = isCurse ? 0xff8888 : 0xffe8a0;
  const chordAlpha  = isCurse ? 0.55 : 0.50;
  const chordColor  = isCurse ? 0x2a0606 : 0x0a0604;
  const r = sizePx / 2;
  const ringR = r * 0.95;
  const ringW = Math.max(1, Math.round(r * 0.09));

  // Outer summoning ring — thick coloured stroke, plus a dim inner "shadow"
  // ring one step in to fake depth.
  g.lineStyle(ringW, ringColor, 1);
  g.strokeCircle(cx, cy, ringR);
  g.fillStyle(chordColor, chordAlpha);
  g.fillCircle(cx, cy, ringR - ringW * 0.5);
  g.lineStyle(1, ringColor, 0.4);
  g.strokeCircle(cx, cy, ringR * 0.78);
  // Four cardinal tick marks on the outer ring — tiny bars at N/S/E/W.
  const tick = Math.max(1, r * 0.10);
  g.lineStyle(Math.max(1, ringW * 0.7), ringColor, 0.85);
  g.beginPath();
  g.moveTo(cx, cy - ringR - tick * 0.4); g.lineTo(cx, cy - ringR + tick * 0.8);
  g.moveTo(cx, cy + ringR - tick * 0.8); g.lineTo(cx, cy + ringR + tick * 0.4);
  g.moveTo(cx - ringR - tick * 0.4, cy); g.lineTo(cx - ringR + tick * 0.8, cy);
  g.moveTo(cx + ringR - tick * 0.8, cy); g.lineTo(cx + ringR + tick * 0.4, cy);
  g.strokePath();

  // Inner symbol — picked from the boon's dominant effect.
  const innerR = ringR * 0.60;
  const kind = pickBoonRuneKind(src);
  switch (kind) {
    case 'sword':    drawRuneSwords(g, cx, cy, innerR, innerColor); break;
    case 'heart':    drawRuneHeart(g, cx, cy, innerR, innerColor); break;
    case 'star':     drawRuneStar(g, cx, cy, innerR, innerColor); break;
    case 'chevron':  drawRuneChevron(g, cx, cy, innerR, innerColor); break;
    case 'triSlash': drawRuneTriSlash(g, cx, cy, innerR, innerColor); break;
    case 'shield':   drawRuneShield(g, cx, cy, innerR, innerColor); break;
    case 'coin':     drawRuneCoin(g, cx, cy, innerR, innerColor); break;
    case 'gem':      drawRuneGem(g, cx, cy, innerR, innerColor); break;
    case 'flame':    drawRuneFlame(g, cx, cy, innerR, isCurse ? innerColor : 0xff8040); break;
    case 'frost':    drawRuneFrost(g, cx, cy, innerR, isCurse ? innerColor : 0x80c4ff); break;
    case 'leaf':     drawRuneLeaf(g, cx, cy, innerR, isCurse ? innerColor : 0x90d070); break;
    case 'shadow':   drawRuneShadow(g, cx, cy, innerR, isCurse ? innerColor : 0xc090e0); break;
    case 'stack':    drawRuneStack(g, cx, cy, innerR, innerColor); break;
    case 'lock':     drawRuneLock(g, cx, cy, innerR, innerColor); break;
    case 'skull':    drawRuneSkull(g, cx, cy, innerR, innerColor); break;
    case 'trilogy':  drawRuneTrilogy(g, cx, cy, innerR, innerColor); break;
  }
}

// ── Rune primitives (each draws at (cx,cy), sized to radius r, tint c) ─────

function drawRuneSwords(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  g.lineStyle(Math.max(2, r * 0.22), c, 1);
  g.beginPath();
  g.moveTo(cx - r * 0.85, cy - r * 0.85); g.lineTo(cx + r * 0.85, cy + r * 0.85);
  g.moveTo(cx + r * 0.85, cy - r * 0.85); g.lineTo(cx - r * 0.85, cy + r * 0.85);
  g.strokePath();
  g.fillStyle(c, 1);
  g.fillCircle(cx - r * 0.85, cy - r * 0.85, Math.max(1.5, r * 0.14));
  g.fillCircle(cx + r * 0.85, cy - r * 0.85, Math.max(1.5, r * 0.14));
}

function drawRuneHeart(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  g.fillStyle(c, 1);
  g.beginPath();
  g.arc(cx - r * 0.40, cy - r * 0.20, r * 0.42, Math.PI, 0, false);
  g.arc(cx + r * 0.40, cy - r * 0.20, r * 0.42, Math.PI, 0, false);
  g.lineTo(cx, cy + r * 0.85);
  g.closePath();
  g.fillPath();
}

function drawRuneStar(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  g.fillStyle(c, 1);
  g.beginPath();
  const pts = 5;
  for (let i = 0; i < pts * 2; i++) {
    const ang = (-Math.PI / 2) + (i * Math.PI / pts);
    const rad = i % 2 === 0 ? r * 0.95 : r * 0.42;
    const px = cx + Math.cos(ang) * rad;
    const py = cy + Math.sin(ang) * rad;
    if (i === 0) g.moveTo(px, py); else g.lineTo(px, py);
  }
  g.closePath();
  g.fillPath();
}

function drawRuneChevron(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  g.lineStyle(Math.max(2, r * 0.22), c, 1);
  g.beginPath();
  // Top chevron
  g.moveTo(cx - r * 0.8, cy - r * 0.05); g.lineTo(cx, cy - r * 0.60); g.lineTo(cx + r * 0.8, cy - r * 0.05);
  // Bottom chevron
  g.moveTo(cx - r * 0.8, cy + r * 0.55); g.lineTo(cx, cy);            g.lineTo(cx + r * 0.8, cy + r * 0.55);
  g.strokePath();
}

function drawRuneTriSlash(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  g.lineStyle(Math.max(2, r * 0.20), c, 1);
  g.beginPath();
  for (let i = 0; i < 3; i++) {
    const off = (i - 1) * r * 0.55;
    g.moveTo(cx - r * 0.55 + off, cy + r * 0.65);
    g.lineTo(cx + r * 0.15 + off, cy - r * 0.65);
  }
  g.strokePath();
}

function drawRuneShield(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  g.fillStyle(c, 0.95);
  g.beginPath();
  g.moveTo(cx - r * 0.75, cy - r * 0.70);
  g.lineTo(cx + r * 0.75, cy - r * 0.70);
  g.lineTo(cx + r * 0.75, cy + r * 0.10);
  g.lineTo(cx,            cy + r * 0.85);
  g.lineTo(cx - r * 0.75, cy + r * 0.10);
  g.closePath();
  g.fillPath();
  g.lineStyle(Math.max(1, r * 0.10), 0x1a0d04, 0.8);
  g.beginPath();
  g.moveTo(cx - r * 0.50, cy - r * 0.20); g.lineTo(cx + r * 0.50, cy - r * 0.20);
  g.moveTo(cx,            cy - r * 0.70); g.lineTo(cx,            cy + r * 0.20);
  g.strokePath();
}

function drawRuneCoin(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  g.fillStyle(c, 1);
  g.fillCircle(cx, cy, r * 0.90);
  g.fillStyle(0x4a2800, 1);
  // Inscribed square — rotated 45° rhombus in the centre.
  g.beginPath();
  g.moveTo(cx, cy - r * 0.55);
  g.lineTo(cx + r * 0.55, cy);
  g.lineTo(cx, cy + r * 0.55);
  g.lineTo(cx - r * 0.55, cy);
  g.closePath();
  g.fillPath();
}

function drawRuneGem(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  g.fillStyle(c, 1);
  g.beginPath();
  g.moveTo(cx, cy - r * 0.90);
  g.lineTo(cx + r * 0.75, cy);
  g.lineTo(cx, cy + r * 0.90);
  g.lineTo(cx - r * 0.75, cy);
  g.closePath();
  g.fillPath();
  // Facet — a bright highlight triangle on the upper-left side.
  g.fillStyle(0xffffff, 0.35);
  g.beginPath();
  g.moveTo(cx, cy - r * 0.90);
  g.lineTo(cx - r * 0.35, cy - r * 0.10);
  g.lineTo(cx - r * 0.15, cy - r * 0.20);
  g.closePath();
  g.fillPath();
}

function drawRuneFlame(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  // Phaser Graphics has no quadraticCurveTo — approximate the flame silhouette
  // with an asymmetric polygon (narrow tapered top, wide lobed base) so it
  // still reads as a drawn flame without relying on curve primitives.
  g.fillStyle(c, 1);
  g.beginPath();
  g.moveTo(cx,              cy - r * 0.95);
  g.lineTo(cx + r * 0.35,   cy - r * 0.55);
  g.lineTo(cx + r * 0.70,   cy - r * 0.10);
  g.lineTo(cx + r * 0.55,   cy + r * 0.45);
  g.lineTo(cx + r * 0.30,   cy + r * 0.80);
  g.lineTo(cx,              cy + r * 0.30);
  g.lineTo(cx - r * 0.30,   cy + r * 0.80);
  g.lineTo(cx - r * 0.55,   cy + r * 0.45);
  g.lineTo(cx - r * 0.70,   cy - r * 0.10);
  g.lineTo(cx - r * 0.35,   cy - r * 0.55);
  g.closePath();
  g.fillPath();
  // Inner highlight — smaller flame-shaped core for depth.
  g.fillStyle(0xffefa0, 0.75);
  g.beginPath();
  g.moveTo(cx,            cy - r * 0.50);
  g.lineTo(cx + r * 0.25, cy - r * 0.15);
  g.lineTo(cx + r * 0.15, cy + r * 0.30);
  g.lineTo(cx,            cy + r * 0.10);
  g.lineTo(cx - r * 0.15, cy + r * 0.30);
  g.lineTo(cx - r * 0.25, cy - r * 0.15);
  g.closePath();
  g.fillPath();
}

function drawRuneFrost(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  g.lineStyle(Math.max(2, r * 0.16), c, 1);
  g.beginPath();
  for (let i = 0; i < 6; i++) {
    const ang = (i * Math.PI) / 3;
    const x2 = cx + Math.cos(ang) * r * 0.90;
    const y2 = cy + Math.sin(ang) * r * 0.90;
    g.moveTo(cx, cy); g.lineTo(x2, y2);
    // Mini-ticks near the tips.
    const bx = cx + Math.cos(ang) * r * 0.55;
    const by = cy + Math.sin(ang) * r * 0.55;
    const lx = Math.cos(ang + Math.PI / 2) * r * 0.18;
    const ly = Math.sin(ang + Math.PI / 2) * r * 0.18;
    g.moveTo(bx - lx, by - ly); g.lineTo(bx + lx, by + ly);
  }
  g.strokePath();
}

function drawRuneLeaf(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  // Pointed oval approximated via a diamond-with-shoulders polygon (no curve
  // primitives in Phaser.Graphics). Narrow at both tips, fat mid-height.
  g.fillStyle(c, 1);
  g.beginPath();
  g.moveTo(cx,            cy - r * 0.95);
  g.lineTo(cx + r * 0.45, cy - r * 0.40);
  g.lineTo(cx + r * 0.70, cy);
  g.lineTo(cx + r * 0.45, cy + r * 0.40);
  g.lineTo(cx,            cy + r * 0.95);
  g.lineTo(cx - r * 0.45, cy + r * 0.40);
  g.lineTo(cx - r * 0.70, cy);
  g.lineTo(cx - r * 0.45, cy - r * 0.40);
  g.closePath();
  g.fillPath();
  // Central vein with two branch strokes for the "leaf" read.
  g.lineStyle(Math.max(1, r * 0.12), 0x1c3a12, 0.9);
  g.beginPath();
  g.moveTo(cx, cy - r * 0.80); g.lineTo(cx, cy + r * 0.80);
  g.moveTo(cx, cy - r * 0.20); g.lineTo(cx + r * 0.45, cy);
  g.moveTo(cx, cy + r * 0.20); g.lineTo(cx - r * 0.45, cy + r * 0.10);
  g.strokePath();
}

function drawRuneShadow(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  g.fillStyle(c, 1);
  // Jagged mandorla — 12-point star-ish silhouette.
  g.beginPath();
  const pts = 12;
  for (let i = 0; i < pts; i++) {
    const ang = (i * 2 * Math.PI) / pts - Math.PI / 2;
    const rad = i % 2 === 0 ? r * 0.95 : r * 0.55;
    const px = cx + Math.cos(ang) * rad;
    const py = cy + Math.sin(ang) * rad;
    if (i === 0) g.moveTo(px, py); else g.lineTo(px, py);
  }
  g.closePath();
  g.fillPath();
  // Eye of the void in the centre — tiny pupil-dot.
  g.fillStyle(0x0a0008, 1);
  g.fillCircle(cx, cy, r * 0.22);
}

function drawRuneStack(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  g.fillStyle(c, 1);
  const barH = r * 0.22;
  const barW = r * 1.40;
  g.fillRect(cx - barW / 2, cy - r * 0.60,                  barW, barH);
  g.fillRect(cx - barW / 2, cy - barH / 2,                   barW, barH);
  g.fillRect(cx - barW / 2, cy + r * 0.60 - barH,            barW, barH);
  g.lineStyle(1, 0x3a1a08, 0.85);
  g.strokeRect(cx - barW / 2, cy - r * 0.60,       barW, barH);
  g.strokeRect(cx - barW / 2, cy - barH / 2,        barW, barH);
  g.strokeRect(cx - barW / 2, cy + r * 0.60 - barH, barW, barH);
}

function drawRuneLock(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  // Shackle (top half-ring).
  g.lineStyle(Math.max(2, r * 0.22), c, 1);
  g.beginPath();
  g.arc(cx, cy - r * 0.15, r * 0.50, Math.PI, 0, false);
  g.strokePath();
  // Body.
  g.fillStyle(c, 0.95);
  g.fillRect(cx - r * 0.70, cy - r * 0.10, r * 1.40, r * 0.95);
  g.lineStyle(Math.max(1, r * 0.10), 0x2a0606, 0.8);
  g.strokeRect(cx - r * 0.70, cy - r * 0.10, r * 1.40, r * 0.95);
  // Keyhole.
  g.fillStyle(0x2a0606, 1);
  g.fillCircle(cx, cy + r * 0.25, r * 0.18);
  g.fillRect(cx - r * 0.07, cy + r * 0.25, r * 0.14, r * 0.40);
}

function drawRuneSkull(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  g.fillStyle(c, 1);
  // Cranium.
  g.fillCircle(cx, cy - r * 0.15, r * 0.75);
  // Jaw — lower trapezoid.
  g.beginPath();
  g.moveTo(cx - r * 0.45, cy + r * 0.35);
  g.lineTo(cx + r * 0.45, cy + r * 0.35);
  g.lineTo(cx + r * 0.30, cy + r * 0.80);
  g.lineTo(cx - r * 0.30, cy + r * 0.80);
  g.closePath();
  g.fillPath();
  // Eye sockets.
  g.fillStyle(0x1a0404, 1);
  g.fillCircle(cx - r * 0.28, cy - r * 0.15, r * 0.18);
  g.fillCircle(cx + r * 0.28, cy - r * 0.15, r * 0.18);
  // Nose.
  g.beginPath();
  g.moveTo(cx, cy + r * 0.05);
  g.lineTo(cx - r * 0.10, cy + r * 0.28);
  g.lineTo(cx + r * 0.10, cy + r * 0.28);
  g.closePath();
  g.fillPath();
}

function drawRuneTrilogy(g: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, c: number): void {
  // Three interlocked dots arranged in a triangle — "harmony" feel.
  g.fillStyle(c, 1);
  g.fillCircle(cx,                cy - r * 0.60, r * 0.30);
  g.fillCircle(cx - r * 0.60,     cy + r * 0.30, r * 0.30);
  g.fillCircle(cx + r * 0.60,     cy + r * 0.30, r * 0.30);
  g.lineStyle(Math.max(1, r * 0.10), c, 0.80);
  g.beginPath();
  g.moveTo(cx,            cy - r * 0.60); g.lineTo(cx - r * 0.60, cy + r * 0.30);
  g.lineTo(cx + r * 0.60, cy + r * 0.30); g.closePath();
  g.strokePath();
}

/** Crossed swords — the Battle / Continue glyph. Two FULL swords (blade,
 *  crossguard, grip, pommel) crossed in an X with **blades pointing UPWARD**,
 *  pommels meeting at the bottom-centre. Drawn with filled shapes for proper
 *  sword silhouettes. Each sword is built in local space then rotated and
 *  translated onto the canvas. */
export function drawBattleCardGlyph(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  tint = 0xc8973a,
): void {
  // Highlight + shade tints derived from the input tint — keeps the sword
  // monochromatic but with bright edge highlight + dark shadow underneath.
  const blade = tint;
  const bladeShine = 0xffe8a0;
  const bladeEdge = 0x6b4a18;
  const grip = 0x4a2810;
  const gripBind = 0x2a1808;

  /** Render one sword whose +U axis (pommel → tip) points along `angDeg`
   *  measured in screen coords (0° = right, 90° = down, 270° = up).
   *  - 225°  → up-LEFT  (sword pointing toward upper-left corner)
   *  - 315°  → up-RIGHT (sword pointing toward upper-right corner)
   *  Both pommels end up at the bottom-centre, both tips at the top corners. */
  const drawOneSword = (angDeg: number): void => {
    const ang = Phaser.Math.DegToRad(angDeg);
    const cosA = Math.cos(ang); const sinA = Math.sin(ang);
    const toX = (u: number, v: number): number => cx + u * cosA - v * sinA;
    const toY = (u: number, v: number): number => cy + u * sinA + v * cosA;

    // Sword measurements (in local-space pixels along the blade axis).
    // Pommel at u = -22 (bottom of sword in local space), tip at u = +22
    // (top in local space). Blade is widest near the crossguard and tapers
    // to a point.
    const POMMEL_U    = -22;
    const GRIP_TOP_U  = -20;  // close to pommel
    const GRIP_BOT_U  = -10;  // close to crossguard
    const GUARD_U     =  -8;
    const BLADE_TOP_U =  -8;  // where blade meets crossguard
    const BLADE_BOT_U =  22;  // tip
    const BLADE_MAX_W = 4;

    // ── Blade — tapered diamond (4 points). Top edge wider, tip a single
    // point. Filled with the main tint then a thin highlight + dark edge.
    const blade1 = { u: BLADE_TOP_U, v: -BLADE_MAX_W };
    const blade2 = { u: BLADE_TOP_U, v:  BLADE_MAX_W };
    const blade3 = { u: BLADE_BOT_U, v: 0 };
    g.fillStyle(blade, 1);
    g.beginPath();
    g.moveTo(toX(blade1.u, blade1.v), toY(blade1.u, blade1.v));
    g.lineTo(toX(blade2.u, blade2.v), toY(blade2.u, blade2.v));
    g.lineTo(toX(blade3.u, blade3.v), toY(blade3.u, blade3.v));
    g.closePath();
    g.fillPath();
    // Centre highlight — thin line from crossguard to tip along v=0.
    g.lineStyle(1, bladeShine, 0.9);
    g.beginPath();
    g.moveTo(toX(BLADE_TOP_U + 1, 0), toY(BLADE_TOP_U + 1, 0));
    g.lineTo(toX(BLADE_BOT_U - 1, 0), toY(BLADE_BOT_U - 1, 0));
    g.strokePath();
    // Dark edge along the long-side of the blade for a forged-steel feel.
    g.lineStyle(1, bladeEdge, 0.7);
    g.beginPath();
    g.moveTo(toX(blade1.u, blade1.v), toY(blade1.u, blade1.v));
    g.lineTo(toX(blade3.u, blade3.v), toY(blade3.u, blade3.v));
    g.moveTo(toX(blade2.u, blade2.v), toY(blade2.u, blade2.v));
    g.lineTo(toX(blade3.u, blade3.v), toY(blade3.u, blade3.v));
    g.strokePath();

    // ── Crossguard — perpendicular bar at u=GUARD_U, ~10 px wide.
    const guard1 = { u: GUARD_U, v: -10 };
    const guard2 = { u: GUARD_U, v:  10 };
    const guard3 = { u: GUARD_U - 2, v:  10 };
    const guard4 = { u: GUARD_U - 2, v: -10 };
    g.fillStyle(blade, 1);
    g.beginPath();
    g.moveTo(toX(guard1.u, guard1.v), toY(guard1.u, guard1.v));
    g.lineTo(toX(guard2.u, guard2.v), toY(guard2.u, guard2.v));
    g.lineTo(toX(guard3.u, guard3.v), toY(guard3.u, guard3.v));
    g.lineTo(toX(guard4.u, guard4.v), toY(guard4.u, guard4.v));
    g.closePath();
    g.fillPath();

    // ── Grip — dark wrapped rectangle at u between GRIP_TOP_U and GRIP_BOT_U.
    g.fillStyle(grip, 1);
    g.beginPath();
    g.moveTo(toX(GRIP_TOP_U,  -2), toY(GRIP_TOP_U,  -2));
    g.lineTo(toX(GRIP_BOT_U,  -2), toY(GRIP_BOT_U,  -2));
    g.lineTo(toX(GRIP_BOT_U,   2), toY(GRIP_BOT_U,   2));
    g.lineTo(toX(GRIP_TOP_U,   2), toY(GRIP_TOP_U,   2));
    g.closePath();
    g.fillPath();
    // Two binding bands across the grip for a leather-wrapped look.
    g.lineStyle(1, gripBind, 1);
    for (const u of [-17, -13]) {
      g.beginPath();
      g.moveTo(toX(u, -2), toY(u, -2));
      g.lineTo(toX(u,  2), toY(u,  2));
      g.strokePath();
    }

    // ── Pommel — small disk at the top of the hilt.
    g.fillStyle(blade, 1);
    g.fillCircle(toX(POMMEL_U, 0), toY(POMMEL_U, 0), 3);
    g.lineStyle(1, bladeEdge, 0.8);
    g.strokeCircle(toX(POMMEL_U, 0), toY(POMMEL_U, 0), 3);
  };

  // Two swords pointing UP, crossed in an X. Pommels meet at bottom-centre,
  // blades fan toward the upper-left and upper-right corners.
  // 225° in screen-space = up-left direction; 315° = up-right.
  drawOneSword(225); // sword pointing up-LEFT
  drawOneSword(315); // sword pointing up-RIGHT (drawn last so it overlaps the centre)
}

/** Three skulls — big central + two smaller flanking. The Elite Pack card. */
export function drawElitePackCardGlyph(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  tint = 0xd04040,
): void {
  const drawSkull = (x: number, y: number, r: number): void => {
    g.fillStyle(0xe8d0b0, 1);
    g.fillCircle(x, y - r * 0.15, r);                         // cranium
    g.fillRect(x - r * 0.7, y + r * 0.55, r * 1.4, r * 0.6);  // jaw
    g.fillStyle(0x1a0a00, 1);
    g.fillCircle(x - r * 0.35, y - r * 0.1, r * 0.25);        // left eye
    g.fillCircle(x + r * 0.35, y - r * 0.1, r * 0.25);        // right eye
    g.fillRect(x - r * 0.1, y + r * 0.25, r * 0.2, r * 0.25); // nose slot
  };
  // Big central skull with two smaller flanking.
  drawSkull(cx, cy + 2, 11);
  drawSkull(cx - 16, cy + 6, 7);
  drawSkull(cx + 16, cy + 6, 7);
  // Red crown above the central skull — communicates "elite pack, not just one".
  g.fillStyle(tint, 0.9);
  g.fillTriangle(cx - 10, cy - 12, cx, cy - 20, cx + 10, cy - 12);
  g.fillTriangle(cx - 7, cy - 10, cx - 4, cy - 16, cx - 1, cy - 10);
  g.fillTriangle(cx + 1, cy - 10, cx + 4, cy - 16, cx + 7, cy - 10);
}

/** Shrine pillar with an aura halo above. The Shrine of Boons card. */
export function drawShrineCardGlyph(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  tint = 0xc8973a,
): void {
  // Pillar base (wide), shaft (narrow), capital (wider than shaft).
  g.fillStyle(tint, 0.95);
  g.fillRect(cx - 14, cy + 14, 28, 5);   // base slab
  g.fillRect(cx - 10, cy + 10, 20, 4);   // upper base
  g.fillRect(cx - 6,  cy - 6, 12, 16);   // shaft
  g.fillRect(cx - 10, cy - 10, 20, 4);   // capital
  // Aura halo — soft concentric rings above the pillar.
  g.lineStyle(1.5, 0xffd060, 0.75);
  g.strokeCircle(cx, cy - 16, 10);
  g.lineStyle(1, 0xffe8a0, 0.55);
  g.strokeCircle(cx, cy - 16, 7);
  // Central gem glow atop the pillar.
  g.fillStyle(0xffffff, 0.9);
  g.fillCircle(cx, cy - 16, 3);
  g.fillStyle(0xffd060, 1);
  g.fillCircle(cx, cy - 16, 2);
}

/** Goblin pack silhouette — one tusked head flanked by two smaller goblins.
 *  The Horde of Goblins card. Green palette (`0x4a8030` default) signals
 *  "goblin faction" at a glance and reads distinct from the red Elite Pack
 *  skulls. Tusks + pointy ears carry the goblin identity at card size. */
export function drawHordeCardGlyph(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  tint = 0x4a8030,
): void {
  const drawGoblin = (x: number, y: number, r: number): void => {
    // Pointy ears — two triangles flanking the head.
    g.fillStyle(tint, 1);
    g.beginPath();
    g.moveTo(x - r * 1.05, y - r * 0.15);
    g.lineTo(x - r * 0.55, y - r * 0.85);
    g.lineTo(x - r * 0.50, y - r * 0.30);
    g.closePath();
    g.fillPath();
    g.beginPath();
    g.moveTo(x + r * 1.05, y - r * 0.15);
    g.lineTo(x + r * 0.55, y - r * 0.85);
    g.lineTo(x + r * 0.50, y - r * 0.30);
    g.closePath();
    g.fillPath();
    // Head — rounded oval.
    g.fillStyle(tint, 1);
    g.fillCircle(x, y, r);
    // Darker outline for readability on the card background.
    g.lineStyle(1, 0x1c3a12, 0.9);
    g.strokeCircle(x, y, r);
    // Two small red eyes — menacing rather than cute.
    g.fillStyle(0xd04040, 1);
    g.fillCircle(x - r * 0.32, y - r * 0.10, Math.max(1, r * 0.13));
    g.fillCircle(x + r * 0.32, y - r * 0.10, Math.max(1, r * 0.13));
    // Tusks — two short ivory triangles jutting up from the mouth.
    g.fillStyle(0xe8d0b0, 1);
    g.beginPath();
    g.moveTo(x - r * 0.22, y + r * 0.30);
    g.lineTo(x - r * 0.30, y + r * 0.60);
    g.lineTo(x - r * 0.12, y + r * 0.45);
    g.closePath();
    g.fillPath();
    g.beginPath();
    g.moveTo(x + r * 0.22, y + r * 0.30);
    g.lineTo(x + r * 0.30, y + r * 0.60);
    g.lineTo(x + r * 0.12, y + r * 0.45);
    g.closePath();
    g.fillPath();
  };
  // Back-row big goblin (the warlord), then two mook flankers in front.
  drawGoblin(cx,          cy - 5, 11); // centre warlord
  drawGoblin(cx - 13,     cy + 8, 7);  // left mook
  drawGoblin(cx + 13,     cy + 8, 7);  // right mook
}

/** Stepped altar with a gold coin floating above it — the Gilded Altar card.
 *  Replaces the caravan wagon on the 4th zone-clear slot; the pillar + coin
 *  motif reads as "offering gold to a shrine" at card size. */
export function drawGildedAltarCardGlyph(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  tint = 0xc8a050,
): void {
  // Three-step pedestal.
  g.fillStyle(tint, 0.95);
  g.fillRect(cx - 18, cy + 14, 36, 5);           // bottom step (widest)
  g.fillRect(cx - 14, cy + 9,  28, 5);           // middle step
  g.fillRect(cx - 10, cy - 6,  20, 15);          // altar body
  // Gilt bands on the altar body for texture.
  g.lineStyle(1, 0x6a4500, 0.9);
  g.strokeRect(cx - 10, cy - 6, 20, 15);
  g.beginPath();
  g.moveTo(cx - 9, cy - 1); g.lineTo(cx + 9, cy - 1);
  g.moveTo(cx - 9, cy + 4); g.lineTo(cx + 9, cy + 4);
  g.strokePath();
  // Offering coin hovering above the altar — filled gold circle with stroke.
  g.fillStyle(0xffd060, 1);
  g.fillCircle(cx, cy - 14, 6);
  g.lineStyle(1, 0x7a5500, 1);
  g.strokeCircle(cx, cy - 14, 6);
  // Inner gem accent on the coin so it reads as valuable, not just a dot.
  g.fillStyle(0x8a4a00, 1);
  g.fillCircle(cx, cy - 14, 2);
  // Subtle glow beneath the coin (two faint rings suggesting the offering
  // transforms into power).
  g.lineStyle(1, 0xffd060, 0.55);
  g.strokeCircle(cx, cy - 14, 9);
  g.lineStyle(1, 0xffe8a0, 0.35);
  g.strokeCircle(cx, cy - 14, 12);
}

/** Wagon / caravan silhouette — two wheels + a tilted canopy. Merchant Caravan card. */
export function drawCaravanCardGlyph(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  tint = 0xb38a5a,
): void {
  // Wagon bed — trapezoid for perspective.
  g.fillStyle(tint, 1);
  g.fillRect(cx - 18, cy + 2, 36, 8);
  // Canopy — rounded top, thicker on near side.
  g.fillStyle(0xd8b07a, 1);
  g.fillRect(cx - 16, cy - 10, 32, 12);
  g.lineStyle(1, 0x4a3010, 0.85);
  g.strokeRect(cx - 16, cy - 10, 32, 12);
  // Canopy ribs — vertical lines suggesting fabric panels.
  g.lineStyle(1, 0x4a3010, 0.5);
  g.beginPath();
  g.moveTo(cx - 8, cy - 10); g.lineTo(cx - 8, cy + 2);
  g.moveTo(cx,     cy - 10); g.lineTo(cx,     cy + 2);
  g.moveTo(cx + 8, cy - 10); g.lineTo(cx + 8, cy + 2);
  g.strokePath();
  // Wheels — solid circle + spoke cross for readability.
  const drawWheel = (wx: number, wy: number): void => {
    g.fillStyle(0x1a0a00, 1);
    g.fillCircle(wx, wy, 6);
    g.fillStyle(tint, 1);
    g.fillCircle(wx, wy, 4);
    g.lineStyle(1, 0x1a0a00, 1);
    g.beginPath();
    g.moveTo(wx - 4, wy); g.lineTo(wx + 4, wy);
    g.moveTo(wx, wy - 4); g.lineTo(wx, wy + 4);
    g.strokePath();
  };
  drawWheel(cx - 11, cy + 14);
  drawWheel(cx + 11, cy + 14);
  // Gold coin on the canopy — reads "merchant".
  g.fillStyle(0xffd060, 1);
  g.fillCircle(cx + 13, cy - 12, 4);
  g.lineStyle(1, 0x7a5500, 1);
  g.strokeCircle(cx + 13, cy - 12, 4);
}



/** Drawn "banned book" glyph — a small closed-book silhouette with a red diagonal slash
 *  across it. Used on the Book Ban HUD debuff icon and anywhere else "book banned"
 *  needs to read at a glance. Sized for a ~22×22 px footprint; cx/cy = visual center.
 *  bookTint = color of the book itself; slashTint = color of the banning slash. */
export function drawBanIcon(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  bookTint = 0x8a6420, slashTint = 0xd03030,
): void {
  // Book body — vertical rectangle (like a book standing upright, cover facing us).
  const W = 12;
  const H = 16;
  const left = cx - W / 2;
  const top  = cy - H / 2;
  g.fillStyle(bookTint, 1);
  g.fillRect(left, top, W, H);
  g.lineStyle(1, bookTint, 1);
  g.strokeRect(left, top, W, H);
  // Spine stripe on the left edge
  g.fillStyle(0x1a0a00, 0.65);
  g.fillRect(left, top, 2, H);
  // Horizontal gilt bands for book cover detail
  g.lineStyle(1, 0xffe8a0, 0.55);
  g.beginPath();
  g.moveTo(left + 3, top + 3); g.lineTo(left + W - 1, top + 3);
  g.moveTo(left + 3, top + H - 3); g.lineTo(left + W - 1, top + H - 3);
  g.strokePath();
  // Red diagonal slash — "banned" — top-right to bottom-left, thicker for weight.
  g.lineStyle(3, slashTint, 1);
  g.beginPath();
  g.moveTo(cx + W / 2 + 2, cy - H / 2 - 2);
  g.lineTo(cx - W / 2 - 2, cy + H / 2 + 2);
  g.strokePath();
  // Inner white highlight on the slash so it reads crisp on dark + book bg.
  g.lineStyle(1, 0xffc0c0, 0.9);
  g.beginPath();
  g.moveTo(cx + W / 2 + 2, cy - H / 2 - 2);
  g.lineTo(cx - W / 2 - 2, cy + H / 2 + 2);
  g.strokePath();
}

/** Drawn "two people" social glyph — two small heads with shoulder arcs below. Sized to
 *  fit in a ~20×16 px footprint. Used on Social Hub buttons in place of the 👥 emoji
 *  (per the project-wide no-emoji-in-UI rule). Cx/cy is the glyph's visual center. */
export function drawSocialIcon(g: Phaser.GameObjects.Graphics, cx: number, cy: number, tint = 0xc8973a): void {
  // Two head circles, offset horizontally. Left head slightly smaller / behind so the
  // group reads as two figures rather than a mirror.
  g.fillStyle(tint, 0.95);
  g.lineStyle(1, tint, 1);
  // Back person (slightly higher, smaller)
  g.fillCircle(cx - 4, cy - 3, 2.5);
  // Front person (lower, larger)
  g.fillCircle(cx + 3, cy - 2, 3);
  // Shoulder arcs — two small semicircles below the heads
  g.beginPath();
  g.arc(cx - 4, cy + 3, 3.5, Math.PI, 0, false);
  g.strokePath();
  g.beginPath();
  g.arc(cx + 3, cy + 4, 4, Math.PI, 0, false);
  g.strokePath();
}

/** Drawn padlock glyph — same shackle + body + keyhole design the consumable-slot lock uses.
 *  Sized ~12 px wide; works both as an icon-corner overlay and as an inline text-prefix glyph.
 *  Never use the 🔒 emoji — always call this so locks match the bronze UI palette. */
export function drawPadlock(g: Phaser.GameObjects.Graphics, cx: number, cy: number, tint = 0xc8973a): void {
  const dark = 0x070504;
  // Shackle (arch)
  g.lineStyle(2, tint, 0.9);
  g.beginPath();
  g.moveTo(cx - 4, cy - 1);
  g.lineTo(cx - 4, cy - 5);
  g.arc(cx, cy - 5, 4, Math.PI, 0, false);
  g.lineTo(cx + 4, cy - 1);
  g.strokePath();
  // Body
  g.fillStyle(tint, 0.85);
  g.fillRect(cx - 6, cy - 1, 12, 8);
  g.lineStyle(1, tint, 1);
  g.strokeRect(cx - 6, cy - 1, 12, 8);
  // Keyhole
  g.fillStyle(dark, 1);
  g.fillCircle(cx, cy + 2, 1.5);
  g.fillRect(cx - 1, cy + 2, 2, 3);
}

// ────────────────────────────────────────────────────────────────────────────
// Rune Stone symbols — drawn via Graphics so the HUD buttons stop relying on
// the old `obj_rune_small` / `obj_rune_large` pixel sprites. Shared helpers so
// the talent-tree Eternal Rune Stone node can render the identical triquetra.
// `r` is the radius the symbol paints inside (caller provides centre + scale).
// ────────────────────────────────────────────────────────────────────────────

/** Base Rune Stone glyph — stage-10 anchor. A clean Tiwaz rune (ᛏ, the
 *  "warrior / honor" rune — upward arrow with angled arms) drawn as a
 *  single filled polygon so there are no overlapping strokes at any
 *  scale. Contrasts with the triquetra of the Eternal: one bold arrow
 *  vs three interwoven arcs. */
export function drawBaseRuneStoneSymbol(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  r: number,
  tint = 0xd4a060,
): void {
  // Tiwaz arrow — vertical shaft + two angled arms forming an upward point.
  // Drawn as a single closed polygon so the strokes never cross themselves.
  const shaftW = r * 0.22;
  const armDrop = r * 0.50;
  const armSpan = r * 0.60;
  g.fillStyle(tint, 1);
  g.beginPath();
  g.moveTo(cx,                        cy - r * 0.88);                 // top point
  g.lineTo(cx + armSpan,              cy - r * 0.88 + armDrop);       // right arm tip
  g.lineTo(cx + armSpan - shaftW * 0.6, cy - r * 0.88 + armDrop + shaftW * 0.5); // right arm inside
  g.lineTo(cx + shaftW,               cy - r * 0.20);                 // right shaft top
  g.lineTo(cx + shaftW,               cy + r * 0.85);                 // right shaft bottom
  g.lineTo(cx - shaftW,               cy + r * 0.85);                 // left shaft bottom
  g.lineTo(cx - shaftW,               cy - r * 0.20);                 // left shaft top
  g.lineTo(cx - armSpan + shaftW * 0.6, cy - r * 0.88 + armDrop + shaftW * 0.5); // left arm inside
  g.lineTo(cx - armSpan,              cy - r * 0.88 + armDrop);       // left arm tip
  g.closePath();
  g.fillPath();
  // Dark outline so the rune reads against a warm background too.
  g.lineStyle(Math.max(1, r * 0.05), 0x2a1a08, 0.7);
  g.strokePath();
}

/** Eternal Rune Stone glyph — stage-30 anchor. Triquetra (three circles arranged
 *  in a triangle) enclosed by an outer ring. Cleaner than the previous version:
 *  no intersecting arcs, no inner-triangle connector, no jostling anchor dot —
 *  just three bold rings on a circle so the silhouette reads at any scale.
 *  Matches the D4-waypoint reference the user shared. The talent-tree Eternal
 *  node draws this same symbol so HUD and tree share one visual language. */
export function drawEternalRuneStoneSymbol(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  r: number,
  tint = 0xe04030,
): void {
  // Outer containment ring.
  g.lineStyle(Math.max(1, r * 0.09), tint, 0.85);
  g.strokeCircle(cx, cy, r * 0.95);
  // Three circles arranged in a triangle. Positioned so their edges JUST
  // kiss at a shared centre point — gives the triquetra read without the
  // stroke-on-stroke crossing that made the old version look busy.
  const off  = r * 0.42;
  const arcR = r * 0.40;
  const circles: [number, number][] = [
    [cx,                                cy - off],                               // top
    [cx - off * Math.cos(Math.PI / 6),  cy + off * Math.sin(Math.PI / 6)],       // lower-left
    [cx + off * Math.cos(Math.PI / 6),  cy + off * Math.sin(Math.PI / 6)],       // lower-right
  ];
  g.lineStyle(Math.max(2, r * 0.13), tint, 1);
  for (const [ax, ay] of circles) g.strokeCircle(ax, ay, arcR);
}

/** Consumable-style button frame — dark fill + thin stroke. Used by the rune
 *  stone buttons so they share the vocabulary of the bottom-bar potion slots.
 *  `active` brightens the border; `locked` fades everything. Returns nothing —
 *  the caller layers the glyph on top with the same (cx, cy). */
export function drawRuneStoneButtonFrame(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number,
  size: number,
  opts: { active?: boolean; locked?: boolean; accent?: number } = {},
): void {
  const accent = opts.accent ?? 0xd4a060;
  const fill = 0x0a0806;
  const border = opts.locked ? 0x2a1500 : opts.active ? accent : 0x3a2200;
  const alpha = opts.locked ? 0.55 : 1;
  g.fillStyle(fill, alpha);
  g.fillRect(cx - size / 2, cy - size / 2, size, size);
  g.lineStyle(opts.active ? 2 : 1, border, alpha);
  g.strokeRect(cx - size / 2, cy - size / 2, size, size);
  // Inner bevel — thin highlight line 2 px inside, makes the frame look
  // chiselled like the potion slots.
  g.lineStyle(1, 0x5a3a1a, alpha * 0.6);
  g.strokeRect(cx - size / 2 + 2, cy - size / 2 + 2, size - 4, size - 4);
}
